import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useFormik } from "formik";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { COMPACT_FORM_MODAL_SX } from "../../utils/formModalStyles";
import { getAllDepartment } from "../../services/departmentService";
import { getAllDoctors } from "../../services/doctorService";
import { getAllStaff } from "../../services/StaffService";
import { createVisit } from "../../services/DoctorVisitService";
import {
  getAssignedStaff,
  getAssignmentByAdmissionId,
} from "../../services/PatientAssignmentService";

const validationSchema = Yup.object({
  visitType: Yup.string().required("Visit type is required"),
  visitedBy: Yup.string().required("Visited by is required"),
  visitDate: Yup.date().required("Visit date is required"), //.nullable(),
  notes: Yup.string().required("Notes are required"),
});

const visitTypeOptions = [
  { key: "doctor", label: "Doctor" },
  { key: "staff", label: "Staff" },
];

const DoctorVisitForm = ({
  open,
  onClose,
  admissionId,
  patientId,
  patient_info = {},
}) => {
  const [doctorList, setDoctorList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [assignedDoctor, setAssignedDoctor] = useState(null);
  const [assignedNurse, setAssignedNurse] = useState([]);

  useEffect(() => {
    fetchDoctors();
    fetchStaff();
  }, []);

  useEffect(() => {
    // Load assigned staff/doctor when modal opens. Prefer admission-based API.
    if (!open) return;
    if (admissionId != null) {
      fetchAssignedByAdmission(admissionId);
    } else if (patientId) {
      fetchAssignedStaff(patientId);
    }
  }, [open, admissionId, patientId]);

  const fetchDoctors = async () => {
    try {
      const data = await getAllDoctors();
      setDoctorList(data);
    } catch (error) {
      console.error("Failed to fetch doctors", error);
    }
  };

  const fetchStaff = async () => {
    try {
      const data = await getAllStaff();
      setStaffList(data);
    } catch (error) {
      console.error("Failed to fetch staff", error);
    }
  };

  const fetchAssignedStaff = async (patientId) => {
    try {
      const res = await getAssignedStaff(patientId);

      if (res?.doctorId) {
        const docId = Number(res.doctorId);
        const byList = Array.isArray(doctorList)
          ? doctorList.find((d) => Number(d.id) === docId)
          : null;
        const name = res?.doctorName || byList?.name || "";
        setAssignedDoctor({ id: docId, name });
      } else {
        setAssignedDoctor(null);
      }

      if (res?.nurseId && res?.nurseName) {
        const nurseIds = res.nurseId.split(",");
        const nurseNames = res.nurseName.split(",");

        const nurseList = nurseIds.map((id, index) => ({
          id: parseInt(id.trim(), 10),
          name: nurseNames[index]?.trim() || "",
        }));

        setAssignedNurse(nurseList);
      } else {
        setAssignedNurse([]);
      }
    } catch (error) {
      console.error("Failed to fetch assigned staff", error);
    }
  };

  const fetchAssignedByAdmission = async (admissionIdParam) => {
    try {
      const res = await getAssignmentByAdmissionId(admissionIdParam);
      console.log("Assignment API response:", res); // ADD THIS
      if (res?.doctorId) {
        setAssignedDoctor({
          id: Number(res.doctorId),
          name: res?.doctorName || "",
        });
      } else {
        setAssignedDoctor(null);
      }

      // if (res?.nurseId && res?.nurseName) {
      //   const nurseIds = String(res.nurseId).split(",");
      //   const nurseNames = String(res.nurseName).split(",");
      //   const nurseList = nurseIds.map((id, index) => ({ id: parseInt(String(id).trim(), 10), name: nurseNames[index]?.trim() || "" }));
      //   setAssignedNurse(nurseList);
      // } else {
      //   setAssignedNurse([]);
      // }

      if (res?.nurseId) {
        const nurseIds = String(res.nurseId).split(",");

        const nurseList = nurseIds.map((id) => {
          const found = staffList.find(
            (s) => Number(s.id) === Number(id.trim()),
          );

          return {
            id: parseInt(id.trim(), 10),
            name: found?.name || "",
          };
        });

        setAssignedNurse(nurseList);
      } else {
        setAssignedNurse([]);
      }
    } catch (error) {
      console.error("Failed to fetch assignment by admission", error);
    }
  };

  // If we got the id but name was missing when assigned staff loaded, fill it once doctorList is ready
  useEffect(() => {
    if (
      assignedDoctor &&
      assignedDoctor.id &&
      !assignedDoctor.name &&
      Array.isArray(doctorList) &&
      doctorList.length
    ) {
      const byList = doctorList.find(
        (d) => Number(d.id) === Number(assignedDoctor.id),
      );
      if (byList && byList.name) {
        setAssignedDoctor((prev) => ({ ...(prev || {}), name: byList.name }));
      }
    }
  }, [doctorList, assignedDoctor]);

  const formik = useFormik({
    initialValues: {
      patientName: patient_info?.name || "",
      visitedBy: "",
      visitedByName: "",
      visitType: "",
      visitDate: null,
      notes: "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          patientAssignmentId: patientId,
          admissionId: admissionId,
          visitedBy: values.visitedBy,
          visitedByName:
            values.visitedByName ||
            (values.visitType === "doctor"
              ? assignedDoctor?.name
              : assignedNurse.find(
                  (n) => String(n.id) === String(values.visitedBy),
                )?.name || ""),
          visitDate: dayjs(values.visitDate).format("YYYY-MM-DDTHH:mm:ss"),
          notes: values.notes,
          visitType: values.visitType,
        };
        await createVisit(payload);
        alert("Doctor Visit saved successfully");
        resetForm();
        onClose();
      } catch (error) {
        console.error("Failed to save visit:", error);
      }
    },
  });

  console.log(assignedNurse);
  const handleClose = () => {
  formik.resetForm();   // ✅ clear all fields
  onClose();            // ✅ close modal
};
 
  return (
    <BasicModal
      open={open}
     onClose={handleClose}
      sx={COMPACT_FORM_MODAL_SX}
    >
      <Box>
        <Typography
          variant="h5"
          sx={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center" }}
          gutterBottom
        >
          Visit Records
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                id="visitType"
                name="visitType"
                label="Visit Type"
                value={formik.values.visitType}
                onChange={(e) => {
                  formik.setFieldValue("visitType", e.target.value);
                  formik.setFieldValue("visitedBy", ""); // reset visitedBy
                  formik.setFieldValue("visitedByName", "");
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.visitType && Boolean(formik.errors.visitType)
                }
                helperText={formik.touched.visitType && formik.errors.visitType}
              >
                {visitTypeOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                id="visitedBy"
                name="visitedBy"
                label="Visited By"
                value={formik.values.visitedBy}
                onChange={(e) => {
                  formik.handleChange(e);
                  const val = e.target.value;
                  if (formik.values.visitType === "doctor" && assignedDoctor) {
                    formik.setFieldValue(
                      "visitedByName",
                      assignedDoctor.name || "",
                    );
                  }
                  if (
                    formik.values.visitType === "staff" &&
                    assignedNurse.length > 0
                  ) {
                    const found = assignedNurse.find(
                      (n) => String(n.id) === String(val),
                    );
                    formik.setFieldValue("visitedByName", found?.name || "");
                  }
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.visitedBy && Boolean(formik.errors.visitedBy)
                }
                helperText={formik.touched.visitedBy && formik.errors.visitedBy}
              >
                {!formik.values.visitType && (
                  <MenuItem value="" disabled>
                    Select visit type first
                  </MenuItem>
                )}

                {formik.values.visitType === "doctor" && assignedDoctor ? (
                  <MenuItem value={String(assignedDoctor.id)}>
                    {assignedDoctor.name}
                  </MenuItem>
                ) : formik.values.visitType === "doctor" ? (
                  <MenuItem disabled>No doctor assigned</MenuItem>
                ) : null}

                {formik.values.visitType === "staff" &&
                assignedNurse.length > 0 ? (
                  assignedNurse.map((nurse) => (
                    <MenuItem key={nurse.id} value={String(nurse.id)}>
                      {nurse.name}
                    </MenuItem>
                  ))
                ) : formik.values.visitType === "staff" ? (
                  <MenuItem disabled>No staff assigned</MenuItem>
                ) : null}
              </TextField>
            </Grid>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid item xs={12}>
                <DatePicker
                  label="Visit Date"
                  value={formik.values.visitDate}
                  onChange={(value) => {
                    formik.setFieldValue("visitDate", value);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      onBlur: formik.handleBlur,
                      error:
                        formik.touched.visitDate &&
                        Boolean(formik.errors.visitDate),
                      helperText:
                        formik.touched.visitDate && formik.errors.visitDate,
                    },
                  }}
                />
              </Grid>
            </LocalizationProvider>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="notes"
                name="notes"
                label="Notes / Remarks"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
              />
            </Grid>

            <Grid item xs={12}>
              <FormModalActions
                onCancel={handleClose}
                submitType="submit"
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default DoctorVisitForm;
